import is from '@sindresorhus/is';
import { motion, useIsPresent } from 'framer-motion';
import { type FunctionComponent, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { TILE_TRANSITION } from './constants';
import {
    useSetTileBox,
    useSetTileSlot,
    useTileErrorBoundary,
    useTileWidthContext,
    useTilingEngineContext,
} from './TilingEngineContext';
import type { TilingEnginePanelProps } from './types';

export const TilingEnginePanel: FunctionComponent<TilingEnginePanelProps> = ({
    children,
    id,
    maxWidth,
    minWidth,
    width,
}) => {
    const { notify } = useTilingEngineContext();
    const setTileSlot = useSetTileSlot();
    const setTileBox = useSetTileBox();
    const tileWidths = useTileWidthContext();
    const ErrorBoundary = useTileErrorBoundary();
    const tileRef = useRef<HTMLDivElement>(null);
    const slotRef = useRef<HTMLDivElement>(null);

    const setOuterRef = useCallback(
        (node: HTMLDivElement | null) => {
            tileRef.current = node;
            setTileBox(id, node);
        },
        [id, setTileBox],
    );

    const isPresent = useIsPresent();
    const wasPresent = useRef(true);

    const [frozenWidth, setFrozenWidth] = useState<number | null>(null);

    const widthStyle = tileWidths.get(id);
    const resolvedWidth = widthStyle?.width ?? width ?? '50%';
    const resolvedMinWidth = widthStyle?.minWidth ?? minWidth ?? 0;

    // biome-ignore lint/correctness/useExhaustiveDependencies: fire once on mount only
    useEffect(() => {
        notify({ type: 'tileOpenStart', tileId: id, animated: false });
        notify({ type: 'tileOpenComplete', tileId: id, animated: false });
    }, []);

    useEffect(() => {
        setTileSlot(id, slotRef.current);

        return () => {
            setTileSlot(id, null);
        };
    }, [id, setTileSlot]);

    useEffect(() => {
        if (wasPresent.current && !isPresent) {
            notify({ type: 'tileCloseStart', tileId: id });
        }

        wasPresent.current = isPresent;
    }, [isPresent, notify, id]);

    // Freeze inner slot width during exit so content doesn't reflow while outer
    // width tweens to zero.
    useLayoutEffect(() => {
        if (wasPresent.current && !isPresent) {
            const node = tileRef.current;

            if (is.nullOrUndefined(node)) {
                return;
            }

            const measured = node.getBoundingClientRect().width;

            if (measured > 0) {
                setFrozenWidth(measured);
            }

            return;
        }

        if (!wasPresent.current && isPresent) {
            setFrozenWidth(null);
        }
    }, [isPresent]);

    return (
        <motion.div
            ref={setOuterRef}
            initial={false}
            animate={{
                minWidth: resolvedMinWidth,
                width: resolvedWidth,
            }}
            exit={{ minWidth: 0, width: 0 }}
            style={{
                flexShrink: 0,
                height: '100%',
                maxWidth: widthStyle?.maxWidth ?? maxWidth,
                // visible while open so floating chat pill can overflow below tile;
                // hidden during exit so frozen inner slot doesn't bleed past.
                overflow: isPresent ? 'visible' : 'hidden',
            }}
            transition={TILE_TRANSITION}
            onAnimationComplete={() => {
                if (!isPresent) {
                    notify({ type: 'tileCloseComplete', tileId: id });
                }
            }}
        >
            <div ref={slotRef} className="h-full" style={{ width: is.number(frozenWidth) ? frozenWidth : '100%' }}>
                {is.nullOrUndefined(ErrorBoundary) ? children : <ErrorBoundary tileId={id}>{children}</ErrorBoundary>}
            </div>
        </motion.div>
    );
};
